import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Pencil, Image as ImageIcon, StopCircle, Trash2, RotateCcw, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  getMyBanners,
  uploadBannerImage,
  validateBannerFile,
  updateBanner,
  endBanner,
  deleteBanner,
  startBannerCheckout,
} from "../lib/bannerApi";

const STATUS_STYLE = {
  active: "border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e]",
  expired: "border-white/20 bg-white/5 text-white/50",
  ended: "border-white/20 bg-white/5 text-white/50",
  draft: "border-white/20 bg-white/5 text-white/50",
  hidden: "border-[#FFB000]/40 bg-[#FFB000]/10 text-[#FFB000]",
  removed: "border-[#ff2a00]/40 bg-[#ff2a00]/10 text-[#ff2a00]",
};

function timeLeft(ends) {
  if (!ends) return null;
  const ms = new Date(ends).getTime() - Date.now();
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return `${d}d ${h}h left`;
  if (h >= 1) return `${h}h ${m}m left`;
  return `${m}m left`;
}

export default function MyBannerAds() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [busyId, setBusyId] = useState(null);
  const [editUrlId, setEditUrlId] = useState(null);
  const [urlVal, setUrlVal] = useState("");

  const load = () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getMyBanners()
      .then(setAds)
      .catch(() => setAds([]))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [user]);

  const active = ads.filter((a) => a.status === "active");
  const past = ads.filter((a) => a.status !== "active");
  const shown = tab === "active" ? active : past;

  const changeImage = async (id, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const err = validateBannerFile(f);
    if (err) return toast.error(err);
    setBusyId(id);
    try {
      const up = await uploadBannerImage(f);
      await updateBanner(id, { imagePath: up.imagePath, displayImagePath: up.displayImagePath });
      toast.success("Image updated");
      load();
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || err2?.message || "Could not update image.");
    } finally {
      setBusyId(null);
    }
  };

  const saveUrl = async (id) => {
    if (!urlVal.trim().toLowerCase().startsWith("https://"))
      return toast.error("Destination must be an HTTPS URL.");
    setBusyId(id);
    try {
      await updateBanner(id, { destinationUrl: urlVal.trim() });
      toast.success("Link updated");
      setEditUrlId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update link.");
    } finally {
      setBusyId(null);
    }
  };

  const doEnd = async (id) => {
    if (!window.confirm("End this ad now? It stops showing immediately with no refund or time credit.")) return;
    setBusyId(id);
    try {
      await endBanner(id);
      toast.success("Ad ended");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end ad.");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this ad? This removes it from your list.")) return;
    setBusyId(id);
    try {
      await deleteBanner(id);
      toast.success("Ad deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete ad.");
    } finally {
      setBusyId(null);
    }
  };

  const runAgain = async (ad) => {
    setBusyId(ad.id);
    try {
      const res = await startBannerCheckout({ bannerAdId: ad.id, destinationUrl: ad.destination_url });
      if (res.admin_free) {
        toast.success("Banner activated free (admin) for 7 days.");
        load();
      } else if (res.native) {
        toast.success("Banner activated for 7 days.");
        load();
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error("Could not start checkout.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not run again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="my-banner-ads-page" className="min-h-[100dvh] bg-[#050505] pb-20 text-white">
      <div className="rr-on-image sticky top-0 z-30 border-b border-white/10 bg-[#050505]/90 px-5 pb-3 pt-6">
        <button
          data-testid="mba-back"
          onClick={() => navigate("/advertise")}
          className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-white/60 transition-colors hover:text-white"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-white">My banner ads</h1>
        <div className="mt-3 flex gap-2">
          {["active", "past"].map((t) => (
            <button
              key={t}
              data-testid={`mba-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${tab === t ? "bg-[#ff2a00] text-white" : "border border-white/15 text-white/70"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-3 px-5 pt-4">
        {user && !loading && (
          <p data-testid="mba-count" className="text-xs text-white/40">
            {shown.length} {tab} banner {shown.length === 1 ? "ad" : "ads"}
          </p>
        )}
        {loading ? (
          <div className="flex justify-center pt-10"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>
        ) : !user ? (
          <p className="rounded-2xl border border-white/10 bg-[#0d0d0d] px-4 py-4 text-sm text-white/55">
            Log in to manage your banner ads.
          </p>
        ) : shown.length === 0 ? (
          <p className="rounded-2xl border border-white/10 bg-[#0d0d0d] px-4 py-6 text-center text-sm text-white/50">
            {tab === "active" ? "No active banner ads." : "No past banner ads."}
          </p>
        ) : (
          shown.map((a) => {
            const moderated = a.status === "removed" || a.status === "hidden";
            const busy = busyId === a.id;
            return (
              <div key={a.id} data-testid={`mba-card-${a.id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-3">
                {a.display_url && (
                  <img src={a.display_url} alt="" className="aspect-[3/1] w-full rounded-xl bg-[#0e0e0e] object-contain" />
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[a.status] || STATUS_STYLE.draft}`}>
                    {a.status}
                  </span>
                  {a.status === "active" && timeLeft(a.ends_at) && (
                    <span className="text-xs text-white/50">{timeLeft(a.ends_at)}</span>
                  )}
                </div>

                {editUrlId === a.id ? (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      data-testid={`mba-url-input-${a.id}`}
                      value={urlVal}
                      onChange={(e) => setUrlVal(e.target.value)}
                      placeholder="https://your-destination.com"
                      className="min-w-0 flex-1 rounded-xl border border-white/10 bg-[#121212] px-3 py-2 text-base sm:text-sm text-white placeholder:text-white/40 focus:outline-none"
                    />
                    <button data-testid={`mba-url-save-${a.id}`} disabled={busy} onClick={() => saveUrl(a.id)} className="rounded-full bg-[#ff2a00] p-2 text-white disabled:opacity-60">
                      <Check className="h-4 w-4" />
                    </button>
                    <button onClick={() => setEditUrlId(null)} className="rounded-full border border-white/15 p-2 text-white/60">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <p className="mt-1 truncate text-xs text-white/50">{a.destination_url}</p>
                )}

                {a.status === "removed" && a.moderation_reason && (
                  <p className="mt-1 text-xs text-[#ff2a00]">Removed: {a.moderation_reason}</p>
                )}
                {a.status === "hidden" && (
                  <p className="mt-1 text-xs text-[#FFB000]">Hidden by moderation.</p>
                )}

                {!moderated && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {a.status === "active" ? (
                      <>
                        <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/80">
                          <ImageIcon className="h-3.5 w-3.5" /> Edit image
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => changeImage(a.id, e)} />
                        </label>
                        <button data-testid={`mba-edit-url-${a.id}`} onClick={() => { setEditUrlId(a.id); setUrlVal(a.destination_url || ""); }} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/80">
                          <Pencil className="h-3.5 w-3.5" /> Edit URL
                        </button>
                        <button data-testid={`mba-end-${a.id}`} disabled={busy} onClick={() => doEnd(a.id)} className="inline-flex items-center gap-1 rounded-full border border-[#ff2a00]/40 px-3 py-1.5 text-xs font-semibold text-[#ff2a00] disabled:opacity-60">
                          <StopCircle className="h-3.5 w-3.5" /> End Ad
                        </button>
                      </>
                    ) : (
                      <>
                        <button data-testid={`mba-run-again-${a.id}`} disabled={busy} onClick={() => runAgain(a)} className="inline-flex items-center gap-1 rounded-full border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-3 py-1.5 text-xs font-bold text-[#ff2a00] disabled:opacity-60">
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Run Again — £9.99
                        </button>
                        <button data-testid={`mba-delete-${a.id}`} disabled={busy} onClick={() => doDelete(a.id)} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/60 disabled:opacity-60">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
